import React, { useState, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { Icon, Button } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import sentSwooshsIcon from 'images/sent_swooshs.svg';
import envelopeIcon from 'images/envelope.svg';
import LinkButton from 'components/shared/LinkButton';
import RenderInBody from 'components/shared/render_in_body';
import Dismissable from 'components/shared/dismissable';
import useAskTheExpert from './useAskTheExpert';
import AskTheExpertButton from './AskTheExpertButton';
import AskTheExpertMessageInput from './AskTheExpertMessageInput';
import useMicroFeedback from '../MicroFeedback/useMicroFeedback';
import { ASK_THE_EXPERT_DESKTOP_MIN_WIDTH } from './constants';

const AskTheExpert = ({
  lesson,
  postUrl,
  sectionId,
  expert,
  expertIconName,
}) => {
  const [open, setOpen] = useState(false);

  const includeParams = useRef({
    section_id: sectionId,
  });

  const { message, setMessage, sent, sending, reset, submit } = useAskTheExpert(
    {
      endpoint: postUrl,
      include: includeParams.current,
    },
  );

  useMicroFeedback(
    useCallback(
      selectedText => {
        if (window.innerWidth >= ASK_THE_EXPERT_DESKTOP_MIN_WIDTH) return;
        includeParams.current.originally_selected_text = selectedText;
        setMessage(
          t('micro_feedback.feedback_template', { selected: selectedText }),
        );
        setOpen(true);
      },
      [setMessage],
    ),
  );

  const closeModal = () => {
    if (!sending) {
      setOpen(false);
      reset();
    }
  };

  return (
    <div className="Expert Expert--mobile">
      <div className="ExpertContainer">
        <AskTheExpertButton
          icon={expertIconName}
          disabled={sent}
          onClick={() => setOpen(value => !value)}
        />
        {open && (
          <RenderInBody>
            <div
              className={classNames('ExpertPopout is-open', sent && 'is-sent')}
            >
              <div className="Modal">
                <Dismissable
                  className="w-fill d-f jc-c"
                  dismissCallback={closeModal}
                >
                  <div className="Modal-container">
                    <div className="ExpertPopout-formContainer">
                      <div className="Modal-header">
                        <Text variant={TEXT_VARIANTS.BODY}>
                          {t('expert.learner.title')}
                        </Text>
                        <button
                          type="button"
                          onClick={closeModal}
                          className="u-resetBtn Modal-closeIcon"
                        >
                          <Icon name="remove" className="Expert-toggle" />
                        </button>
                      </div>
                      <div className="ExpertForm">
                        <AskTheExpertMessageInput
                          lessonTitle={lesson.title}
                          onChange={setMessage}
                          disabled={sending}
                          value={message}
                        />
                        <div className="ExpertForm-buttonGroup">
                          <Button
                            tall
                            customColor
                            className="ExpertForm-submit"
                            disabled={!message.trim()}
                            loading={sending}
                            onClick={submit}
                          >
                            {t('expert.learner.submit', { name: expert.name })}
                          </Button>
                          <LinkButton
                            disabled={sending}
                            onClick={closeModal}
                            className="ExpertForm-cancel Expert-toggle td-n"
                          >
                            <Text variant={TEXT_VARIANTS.BODY}>
                              {t('expert.cancel')}
                            </Text>
                          </LinkButton>
                        </div>
                      </div>
                    </div>
                    <div className="ExpertPopout-successContainer">
                      <div className="ExpertPopout-image">
                        <img
                          src={envelopeIcon}
                          alt={t('expert.learner.sent_body')}
                        />
                      </div>
                      <div className="ExpertPopout-waves">
                        <img src={sentSwooshsIcon} alt="" />
                      </div>
                      <div className="ExpertPopout-body">
                        <Text component="p" variant={TEXT_VARIANTS.BODY_BOLD}>
                          {t('expert.learner.sent_title')}
                        </Text>
                        <Text component="p" variant={TEXT_VARIANTS.BODY}>
                          {t('expert.learner.sent_body')}
                        </Text>
                        <Button onClick={closeModal}>
                          {t('expert.close')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Dismissable>
                <div className="Modal-background Expert-toggle" />
              </div>
            </div>
          </RenderInBody>
        )}
      </div>
    </div>
  );
};

// react-on-rails workaround to get hooks to work
export default props => <AskTheExpert {...props} />;
